import { useMemo, useRef, useState } from 'react';
import { compact, debounce, isArray, isEqual, uniqBy, uniqWith } from 'lodash';
import { ChipTypeMap } from '@mui/material';
import AutoComplete, { AutoCompleteProps } from './AutoComplete';

export interface AsyncAutoCompleteProps<
    T,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
    ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent']
> extends Omit<
        AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>,
        'options'
    > {
    request: Function;
    customRequestParams?: any;
    searchKey?: string;
    // eslint-disable-next-line no-unused-vars
    optionFactory?: (option: T) => T;
    // eslint-disable-next-line no-unused-vars
    pasteHandler?: (text: string) => Promise<T | null>;
}

const URL_RE = /^https?:\/\//;

const AsyncAutoComplete = <
    T extends { name: string; disabled?: boolean; id: string },
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
    ChipComponent extends React.ElementType
>({
    value,
    request,
    customRequestParams = {},
    searchKey = 'search',
    optionFactory,
    pasteHandler,
    ...rest
}: AsyncAutoCompleteProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>) => {
    const [asyncOptions, setAsyncOptions] = useState(value ? [value] : []);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const currentSearch = useRef('');
    const loadingNext = useRef(false);
    const nextRef = useRef<string | null>(null);
    const optionsLoadedRef = useRef(false);
    const listboxRef = useRef<HTMLElement | null>(null);

    const pasteHandlerRef = useRef(pasteHandler);
    pasteHandlerRef.current = pasteHandler;

    const {
        setFieldValue,
        name,
        keysToLabel = [],
        ListboxProps: ListboxPropsFromRest,
        ...restWithoutListboxProps
    } = rest as any;

    const setFieldValueRef = useRef(setFieldValue);
    setFieldValueRef.current = setFieldValue;
    const nameRef = useRef(name);
    nameRef.current = name;

    const loadOptions = (search: string, q = {}) =>
        request(null, null, { ...customRequestParams, [searchKey]: search || null, ...q });

    const getOptions = async (search: string = '') => {
        nextRef.current = null;
        setIsSearching(true);
        try {
            const data = await loadOptions(search);
            nextRef.current = 'next' in data ? data.next : null;
            currentSearch.current = search;
            optionsLoadedRef.current = true;

            setAsyncOptions(
                uniqWith(
                    compact([
                        ...('results' in data ? data.results : data),
                        ...(isArray(value) ? value : [value])
                    ]),
                    (obj, oth) =>
                        'id' in obj && 'id' in oth ? obj.id === oth.id : isEqual(obj, oth)
                ).map((option) => (optionFactory ? optionFactory(option) : option))
            );
        } finally {
            setIsSearching(false);
        }
    };

    const loadNext = async () => {
        const currentNext = nextRef.current;
        if (!currentNext || loadingNext.current) return;

        loadingNext.current = true;
        setIsLoadingMore(true);
        try {
            const nextParams = new URL(currentNext, window.location.origin).searchParams;
            const data = await loadOptions(
                currentSearch.current,
                Object.fromEntries(nextParams.entries())
            );
            nextRef.current = 'next' in data ? data.next : null;
            const savedScrollTop = listboxRef.current?.scrollTop;
            setAsyncOptions((prevState) =>
                uniqBy([...prevState, ...('results' in data ? data.results : data)], 'id').map(
                    (option) => (optionFactory ? optionFactory(option) : option)
                )
            );
            if (savedScrollTop !== undefined) {
                requestAnimationFrame(() => {
                    if (listboxRef.current) listboxRef.current.scrollTop = savedScrollTop;
                });
            }
        } finally {
            loadingNext.current = false;
            setIsLoadingMore(false);
        }
    };

    const onPasteInput: React.ClipboardEventHandler<HTMLInputElement> = async (e) => {
        const handler = pasteHandlerRef.current;
        if (!handler) return;
        const text = e.clipboardData.getData('text');
        if (!URL_RE.test(text)) return;

        e.preventDefault();
        setIsSearching(true);
        try {
            const resolved = await handler(text);
            if (resolved) {
                setAsyncOptions((prev) => uniqBy([resolved, ...prev], 'id'));
                setFieldValueRef.current?.(nameRef.current, resolved);
                optionsLoadedRef.current = true;
            }
        } finally {
            setIsSearching(false);
        }
    };

    const onInput = useMemo(
        () =>
            debounce(
                (e) => {
                    const { value: inputValue } = e.target;
                    if (inputValue !== currentSearch.current) {
                        getOptions(inputValue);
                    }
                },
                500,
                { trailing: true, leading: false }
            ),
        []
    );

    const onScroll: React.DOMAttributes<HTMLUListElement>['onScroll'] = (ev) => {
        const el = ev.currentTarget;
        listboxRef.current = el;
        const { scrollTop, scrollHeight, clientHeight } = el;

        if (scrollTop + clientHeight >= scrollHeight - 80) {
            loadNext();
        }
    };

    const resolveField = (val: any): string =>
        val && typeof val === 'object' ? val.name ?? '' : val ?? '';

    const renderOption = (props: any, option: any) => {
        const index = props['data-option-index'] ?? 0;
        const label = compact([
            option?.name,
            ...(keysToLabel as string[]).map((key) => resolveField(option[key]))
        ]).join(', ');
        return (
            <li {...props}>
                <span style={{ opacity: 0.4, minWidth: 24, flexShrink: 0 }}>{index + 1}.</span>
                {label}
            </li>
        );
    };

    return (
        <AutoComplete
            value={value}
            options={asyncOptions}
            disabled={isSearching || isLoadingMore}
            name={name}
            keysToLabel={keysToLabel}
            setFieldValue={setFieldValue}
            onInput={onInput}
            onPasteInput={onPasteInput}
            onOpen={() => {
                if (!optionsLoadedRef.current) getOptions();
            }}
            ListboxProps={{ ...ListboxPropsFromRest, onScroll }}
            filterOptions={(x) => x}
            renderOption={renderOption}
            {...restWithoutListboxProps}
        />
    );
};

export default AsyncAutoComplete;
